import React from "react";
import { AccordionDetails } from "@mui/material";
import { StyledAccordion, InnerStyledAccordion } from "../../StyledComponents";
import { renderHistorySubSection } from "../registry/historySectionRegistry";
import { useDoctorConsoleConfig } from "../ConfigProvider";
import { resolveSectionFieldLayout, resolveSubFieldLayouts } from "../engine/resolveSectionFields";
import { resolveStoreData, buildStores } from "../engine/resolveStoreData";
import { sectionRegistry } from "../registry/sectionRegistry";
import { previewRegistry } from "../registry/previewRegistry";

const ConfigurableSectionRenderer = ({
  sectionKey,
  sectionConfig,
  activeSection,
  suggestions,
  isSectionExpanded = () => true,
  handleOuterAccordionChange,
  selectedSectionsData,
  updateSectionData,
  medicalBackground,
  updateMedicalBackground,
  vitals,
  setVitals,
  dentalData,
  setDentalData,
  bloodGroup,
  setBloodGroup,
  aiSuggestionToggle,
  ruleSuggestionToggle,
  handleCrossButtonClick,
  handleStartRecordingForIndividualSection,
  handleStopRecordingForIndividualSection,
  isAiEnabled,
  routeAutoData,
  followUpError,
  referralError,
  setFollowUpError,
  setReferralError,
  state,
  isS3ModalOpen,
  setIsS3ModalOpen,
  activeHistorySection,
  setActiveHistorySection,
  sectionDependencies = {},
}) => {
  const { visibleHistorySubSections, config, ruleContext } = useDoctorConsoleConfig();

  const sectionName = sectionKey;
  const title = sectionConfig?.label ?? sectionName;
  const isExpanded = isSectionExpanded(sectionName);
  const isActive = activeSection === sectionName;

  // ─── build stores map (data + setter per store id) ────────────────────────
  const stores = buildStores({
    selectedSectionsData,
    updateSectionData,
    medicalBackground,
    updateMedicalBackground,
    vitals,
    setVitals,
    dentalData,
    setDentalData,
    bloodGroup,
    setBloodGroup,
  });

  // ─── resolve field layout from config ─────────────────────────────────────
  const fieldLayout = sectionConfig?.fieldSet
    ? resolveSectionFieldLayout(sectionConfig, config, ruleContext)
    : null;
  const subFieldLayouts = sectionConfig?.subFieldSets
    ? resolveSubFieldLayouts(sectionConfig, config, ruleContext)
    : null;

  // ─── bundle all non-store runtime props ───────────────────────────────────
  const extras = {
    suggestions,
    aiSuggestionToggle,
    ruleSuggestionToggle,
    handleCrossButtonClick,
    handleStartRecordingForIndividualSection,
    handleStopRecordingForIndividualSection,
    isAiEnabled,
    routeAutoData,
    followUpError,
    referralError,
    setFollowUpError,
    setReferralError,
    state,
    isS3ModalOpen,
    setIsS3ModalOpen,
    sectionDependencies,
  };

  // ─── 1. registry-based rendering (all named sections) ─────────────────────
  const rendererComponent = sectionConfig?.renderer?.component ?? sectionName;
  const entry = sectionRegistry[rendererComponent];

  if (entry) {
    const { Component, wrapDetails = true, buildProps, buildActions } = entry;

    // Resolve section data from config-driven store binding
    const storeData = resolveStoreData(sectionConfig, stores);
    const meta = { sectionName, isActive, isExpanded, fieldLayout, subFieldLayouts };
    const componentProps = buildProps(storeData, meta, extras, stores);
    const actions = buildActions ? buildActions(extras, isExpanded) : null;

    // Resolve header preview component from config
    const previewKey = sectionConfig?.headerPreview?.component;
    const PreviewComponent = previewKey ? previewRegistry[previewKey] : null;
    const preview = PreviewComponent ? (
      <PreviewComponent
        data={storeData.data}
        fieldLayout={fieldLayout}
        stores={stores}
        storeData={storeData}
        extras={extras}
      />
    ) : null;

    return (
      <StyledAccordion
        title={title}
        expanded={isExpanded}
        onChange={handleOuterAccordionChange(sectionName)}
        headerContent={!isExpanded && preview}
        actions={actions}
      >
        {wrapDetails ? (
          <AccordionDetails>
            <Component {...componentProps} />
          </AccordionDetails>
        ) : (
          <Component {...componentProps} />
        )}
      </StyledAccordion>
    );
  }

  // ─── 2. history container (nested inner accordions) ───────────────────────
  if (
    sectionConfig?.renderer?.type === "historyContainer" ||
    sectionName === "History"
  ) {
    const historyRuntime = {
      medicalBackground,
      updateMedicalBackground,
      selectedSectionsData,
      updateSectionData,
      commonProps: {
        activeHistorySection,
        setActiveHistorySection,
        startRecording: handleStartRecordingForIndividualSection,
        stopRecording: handleStopRecordingForIndividualSection,
        isAiEnabled,
      },
      dateForAppointment: state?.dateForAppointment,
    };

    return (
      <StyledAccordion
        title={title}
        expanded={isExpanded}
        onChange={handleOuterAccordionChange(sectionName)}
      >
        {(isExpanded || isActive) &&
          visibleHistorySubSections.map((subSection) => (
            <InnerStyledAccordion key={subSection.key} title={subSection.label}>
              {renderHistorySubSection(subSection, {
                ...historyRuntime,
                config,
                ruleContext,
              })}
            </InnerStyledAccordion>
          ))}
      </StyledAccordion>
    );
  }

  return null;
};

export default ConfigurableSectionRenderer;
